import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import * as d3 from 'd3';
import { HourlyForecastTimelineSegment } from '../../models/HourlyForecastTimelineData';
import { OperationalReplayData, OperationalReplayPanel } from '../../hooks/timeline/useOperationalReplayData';
import { formatVisibility } from '../../utils/visibilityFormatters';
import {
  getSegmentColor, getVisibilityLabel, getVisibilityColor,
  NO_DATA_COLOR, NOW_LINE_COLOR,
} from '../../utils/timelineVisibility';
import VerificationDebugHover from './VerificationDebugHover';

interface DebugHoverContext {
  segment: HourlyForecastTimelineSegment | null;
  rowLabel: string;
  isActuals: boolean;
  requestedTime: Date | null;
  intervalMinutes: number;
}

interface OperationalReplayTimelineProps {
  data: OperationalReplayData;
  useUTCTime: boolean;
}

interface HoverInfo {
  time: Date;
  x: number;
  y: number;
  segment: HourlyForecastTimelineSegment | null;
  rowLabel: string;
  isActuals: boolean;
  intervalMinutes: number;
}

interface PanelProps {
  panel: OperationalReplayPanel;
  useUTCTime: boolean;
  now: Date;
  actualsInterval: 30 | 60;
  onHover: (info: HoverInfo | null) => void;
  isDebugMode: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

const ReplayPanel: React.FC<PanelProps> = ({ panel, useUTCTime, now, actualsInterval, onHover, isDebugMode, containerRef }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [localDebug, setLocalDebug] = useState<DebugHoverContext | null>(null);
  const [localDebugVisible, setLocalDebugVisible] = useState(true);

  const getHours = (d: Date): number => useUTCTime ? d.getUTCHours() : d.getHours();
  const getMinutes = (d: Date): number => useUTCTime ? d.getUTCMinutes() : d.getMinutes();

  const formatTime = (d: Date): string => {
    return `${getHours(d).toString().padStart(2, '0')}:${getMinutes(d).toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 60, bottom: 35, left: 130 };
    const rowHeight = 40;
    const rowGap = 20;
    const lineExtension = Math.floor(rowHeight / 3);
    const nowLineWidth = 3;
    const borderColor = 'rgba(255, 255, 255, 0.3)';

    const actualsCellWidth = 128;
    const predCellWidth = actualsCellWidth * (60 / actualsInterval);
    const numActualsCells = (4 * 60) / actualsInterval;
    const numPredCells = 4;

    const contentWidth = numActualsCells * actualsCellWidth;
    const totalContentHeight = rowHeight * 2 + rowGap;
    const width = contentWidth + margin.left + margin.right;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', totalContentHeight + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${width} ${totalContentHeight + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const startTime = panel.issueTime;
    const endTime = panel.windowEnd;

    const timeScale = d3.scaleTime()
      .domain([startTime, endTime])
      .range([0, contentWidth]);

    // Build cells: forecast (top, hourly) and actuals (bottom, actualsInterval)
    const forecastIncrements: Date[] = [];
    for (let i = 0; i < numPredCells; i++) {
      forecastIncrements.push(new Date(startTime.getTime() + i * 60 * 60 * 1000));
    }
    const actualsIncrements: Date[] = [];
    for (let i = 0; i < numActualsCells; i++) {
      actualsIncrements.push(new Date(startTime.getTime() + i * actualsInterval * 60 * 1000));
    }

    const hourMarkers: Date[] = [];
    for (let i = 0; i <= 4; i++) {
      hourMarkers.push(new Date(startTime.getTime() + i * 60 * 60 * 1000));
    }

    const findSegment = (segments: HourlyForecastTimelineSegment[], time: Date, interval: number): HourlyForecastTimelineSegment | null => {
      if (interval === 30) {
        const halfInterval = 15 * 60 * 1000;
        return segments.find(s => Math.abs(s.time.getTime() - time.getTime()) <= halfInterval) || null;
      }
      const targetHour = useUTCTime ? time.getUTCHours() : time.getHours();
      return segments.find(s => {
        const segHour = useUTCTime ? s.time.getUTCHours() : s.time.getHours();
        return segHour === targetHour;
      }) || null;
    };

    const rows = [
      {
        label: 'Forecast',
        rowIndex: 0,
        segments: panel.forecastSegments,
        increments: forecastIncrements,
        cellWidth: predCellWidth,
        interval: 60,
        isActuals: false,
      },
      {
        label: 'Actuals',
        rowIndex: 1,
        segments: panel.actualsSegments,
        increments: actualsIncrements,
        cellWidth: actualsCellWidth,
        interval: actualsInterval,
        isActuals: true,
      },
    ];

    rows.forEach(row => {
      const rowY = row.rowIndex * (rowHeight + rowGap);

      svg.append('text')
        .attr('x', -12)
        .attr('y', rowY + rowHeight / 2)
        .attr('text-anchor', 'end')
        .attr('fill', '#ffffff')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('dominant-baseline', 'central')
        .text(row.label);

      // Bright border for actuals row
      if (row.isActuals) {
        svg.append('rect')
          .attr('x', -1)
          .attr('y', rowY - 1)
          .attr('width', contentWidth + 2)
          .attr('height', rowHeight + 2)
          .attr('fill', 'none')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2)
          .attr('rx', 2);
      }

      row.increments.forEach((time, cellIndex) => {
        const segment = findSegment(row.segments, time, row.interval);
        const x = cellIndex * row.cellWidth;
        const color = segment ? getSegmentColor(segment) : NO_DATA_COLOR;

        svg.append('rect')
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', row.cellWidth)
          .attr('height', rowHeight)
          .attr('fill', color);

        svg.append('rect')
          .attr('class', `cell-border r${row.rowIndex}c${cellIndex}`)
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', row.cellWidth)
          .attr('height', rowHeight)
          .attr('fill', 'none')
          .attr('stroke', borderColor)
          .attr('stroke-width', 1);

        svg.append('rect')
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', row.cellWidth)
          .attr('height', rowHeight)
          .attr('fill', 'transparent')
          .attr('cursor', 'pointer')
          .on('mouseenter', function (event) {
            svg.selectAll(`.cell-border.r${row.rowIndex}c${cellIndex}`)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3);

            if (isDebugMode) {
              setLocalDebug({
                segment: segment || null,
                rowLabel: row.label,
                isActuals: row.isActuals,
                requestedTime: row.isActuals ? null : panel.issueTime,
                intervalMinutes: row.interval,
              });
              setLocalDebugVisible(true);
            }

            const containerBounds = containerRef.current?.getBoundingClientRect();
            if (containerBounds) {
              onHover({
                time,
                x: event.clientX - containerBounds.left,
                y: event.clientY - containerBounds.top,
                segment: segment || null,
                rowLabel: row.label,
                isActuals: row.isActuals,
                intervalMinutes: row.interval,
              });
            }
          })
          .on('mouseleave', function () {
            svg.selectAll(`.cell-border.r${row.rowIndex}c${cellIndex}`)
              .attr('stroke', borderColor)
              .attr('stroke-width', 1);
            onHover(null);
          });
      });
    });

    // Hour gridlines + labels
    hourMarkers.forEach((marker, i) => {
      const x = timeScale(marker);
      const isEdge = i === 0 || i === 4;
      svg.append('line')
        .attr('x1', x)
        .attr('y1', -lineExtension)
        .attr('x2', x)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', isEdge ? 2.5 : 1.5)
        .attr('stroke-opacity', isEdge ? 1 : 0.8);

      svg.append('text')
        .attr('x', x)
        .attr('y', totalContentHeight + lineExtension + 18)
        .attr('text-anchor', 'middle')
        .attr('fill', '#ffffff')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .text(formatTime(marker));
    });

    // Now-line if it falls inside this panel's window
    if (now.getTime() >= startTime.getTime() && now.getTime() <= endTime.getTime()) {
      const nowPosition = timeScale(now);
      const nowTimeString = `Now - ${formatTime(now)}`;

      const tempText = svg.append('text')
        .attr('x', 0).attr('y', 0)
        .attr('font-size', '13px').attr('font-weight', 'bold')
        .style('visibility', 'hidden')
        .text(nowTimeString);
      const textBBox = tempText.node()?.getBBox() || { width: 100, height: 20 };
      tempText.remove();

      const labelPadding = { x: 6, y: 4 };
      const labelHeight = textBBox.height + labelPadding.y * 2;
      const labelWidth = textBBox.width + labelPadding.x * 2;
      const labelY = -lineExtension - 30;

      svg.append('rect')
        .attr('x', nowPosition - (labelWidth / 2))
        .attr('y', labelY)
        .attr('width', labelWidth)
        .attr('height', labelHeight)
        .attr('rx', labelHeight / 2)
        .attr('ry', labelHeight / 2)
        .attr('fill', NOW_LINE_COLOR);

      svg.append('text')
        .attr('x', nowPosition)
        .attr('y', labelY + (labelHeight / 2))
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('dominant-baseline', 'central')
        .text(nowTimeString);

      svg.append('line')
        .attr('x1', nowPosition)
        .attr('y1', labelY + labelHeight)
        .attr('x2', nowPosition)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', NOW_LINE_COLOR)
        .attr('stroke-width', nowLineWidth + 3)
        .attr('stroke-opacity', 0.3)
        .attr('stroke-linecap', 'round');

      svg.append('line')
        .attr('x1', nowPosition)
        .attr('y1', labelY + labelHeight)
        .attr('x2', nowPosition)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', NOW_LINE_COLOR)
        .attr('stroke-width', nowLineWidth)
        .attr('stroke-linecap', 'round');
    }
  }, [panel, useUTCTime, now, actualsInterval, onHover, isDebugMode, containerRef]);

  const totalSvgHeight = 40 + 40 * 2 + 20 + 35 + 40;

  const formatTimeRange = (startTime: Date, intervalMinutes: number): string => {
    const endTime = new Date(startTime.getTime() + (intervalMinutes - 1) * 60 * 1000 + 59 * 1000);
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // Compute offset relative to centerPoint (now rounded to the nearest hour),
  // so offsets are always clean multiples of 4.
  const formatOffset = (time: Date): string => {
    // Find offset relative to nearest clean hour from now
    const nowRounded = new Date(now);
    if (useUTCTime) {
      nowRounded.setUTCMinutes(0, 0, 0);
    } else {
      nowRounded.setMinutes(0, 0, 0);
    }
    const diffH = Math.round((time.getTime() - nowRounded.getTime()) / (60 * 60 * 1000));
    if (diffH === 0) return 'NOW';
    return `-${Math.abs(diffH)} h`;
  };

  const startLabel = formatTime(panel.issueTime);
  const startOffset = formatOffset(panel.issueTime);
  const endLabel = formatTime(panel.windowEnd);
  const endOffset = formatOffset(panel.windowEnd);
  const isEndNow = endOffset === 'NOW';

  return (
    <Box sx={{ position: 'relative', mb: 2.5 }}>
      {/* Window header bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mx: '130px',
        mr: '60px',
        mb: 0,
      }}>
        {/* Start time */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexShrink: 0 }}>
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '18px', fontFamily: 'monospace' }}>
            {startLabel}
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px', fontWeight: 500 }}>
            {startOffset}
          </Typography>
        </Box>

        {/* Connecting line */}
        <Box sx={{
          flex: 1,
          height: '1px',
          mx: 1.5,
          background: 'linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0.08))',
        }} />

        {/* End time */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexShrink: 0 }}>
          <Typography sx={{
            color: isEndNow ? '#ba68c8' : '#ffffff',
            fontWeight: 700,
            fontSize: '18px',
            fontFamily: 'monospace',
          }}>
            {endLabel}
          </Typography>
          <Typography sx={{
            color: isEndNow ? '#ba68c8' : 'rgba(255, 255, 255, 0.45)',
            fontSize: '12px',
            fontWeight: isEndNow ? 700 : 500,
          }}>
            {endOffset}
          </Typography>
        </Box>
      </Box>
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: `${totalSvgHeight}px`,
          minWidth: 0,
        }}
      />
      {isDebugMode && localDebug && (
        <VerificationDebugHover
          lastHoveredSegment={localDebug.segment}
          rowLabel={localDebug.rowLabel}
          isActuals={localDebug.isActuals}
          requestedTime={localDebug.requestedTime}
          segmentInterval={localDebug.intervalMinutes}
          formatTimeRange={formatTimeRange}
          visible={localDebugVisible}
          onDismiss={() => setLocalDebugVisible(false)}
        />
      )}
    </Box>
  );
};

const OperationalReplayTimeline: React.FC<OperationalReplayTimelineProps> = ({ data, useUTCTime }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const isDebugMode = useMemo(() =>
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'true',
  []);

  const getHours = (d: Date): number => useUTCTime ? d.getUTCHours() : d.getHours();
  const getMinutes = (d: Date): number => useUTCTime ? d.getUTCMinutes() : d.getMinutes();

  const formatTime = (d: Date): string =>
    `${getHours(d).toString().padStart(2, '0')}:${getMinutes(d).toString().padStart(2, '0')}`;

  const formatTimeRange = (startTime: Date, intervalMinutes: number): string => {
    const endTime = new Date(startTime.getTime() + (intervalMinutes - 1) * 60 * 1000 + 59 * 1000);
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  if (data.panels.length === 0) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        overflowX: 'auto',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 1,
      }}
    >
      {data.panels.map(panel => (
        <ReplayPanel
          key={panel.issueTime.getTime()}
          panel={panel}
          useUTCTime={useUTCTime}
          now={data.referenceTime}
          actualsInterval={data.segmentInterval}
          onHover={setHoverInfo}
          isDebugMode={isDebugMode}
          containerRef={containerRef}
        />
      ))}

      {hoverInfo && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            left: `${Math.min(hoverInfo.x + 15, containerRef.current?.clientWidth ? containerRef.current.clientWidth - 200 : hoverInfo.x)}px`,
            top: `${Math.max(hoverInfo.y - 100, 0)}px`,
            padding: 1.5,
            minWidth: 200,
            zIndex: 1000,
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
            {hoverInfo.rowLabel}: {formatTimeRange(hoverInfo.time, hoverInfo.intervalMinutes)}
          </Typography>
          {hoverInfo.segment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              {hoverInfo.isActuals ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_hmean),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)',
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Harmonic mean: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_hmean)} (displayed)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_median),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)',
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Median: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_median)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.lowest_visibility_level),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)',
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Range: {formatVisibility(hoverInfo.segment.lowest_visibility_level)} - {formatVisibility(hoverInfo.segment.highest_visibility_level)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 16, height: 16,
                    backgroundColor: getSegmentColor(hoverInfo.segment),
                    mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)',
                  }} />
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    Visibility level: {getVisibilityLabel(hoverInfo.segment)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {!hoverInfo.segment && (
            <Typography variant="body2" sx={{ fontSize: '14px', color: 'text.secondary' }}>
              No data
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default OperationalReplayTimeline;
